'use client';

import { classes } from '@/next-utils/css-utils/scss-utils';
import { msg } from '@/services/isomorphic/I18NService';
import { KeyboardEvent, useRef, useState, type ChangeEvent } from 'react';
import { useSearchBox } from '@/react/hooks/useSearchBox';
import { observer } from 'mobx-react-lite';
import type { ISearchSuggestions } from '@/services/models/SearchSuggestions';
import { SearchBoxPopover } from './internal/SearchBoxPopover';
import { Button } from '../../core-ui/Button';
import { Icon, IconSizeProp, IconTypes } from '../../core-ui/Icon';

import S from './styles.base.module.scss';
import { search_box_title } from "@/lang/__generated__/ahnu/search_box_title";
import { search_box_placeholder } from "@/lang/__generated__/ahnu/search_box_placeholder";
import { search_box_clear } from "@/lang/__generated__/ahnu/search_box_clear";
import { search_box_search } from "@/lang/__generated__/ahnu/search_box_search";

/**
 * Properties for the SearchBox component.
 */
export interface ISearchBoxProps {
  /** String of classes for className. */
  className?: string;

  /**
   * Style type of search box.
   *
   * @deprecated -- According to {@link https://www.figma.com/design/rpvXYGjPl3YKghFGHxvcxO/Internal-Search-Workshop?node-id=199-1984&p=f&m=dev}
   * and {@link https://deckersbrand.atlassian.net/browse/HP-4660} it looks like we are not supporting
   * two variants of search box anymore.
   *
   * I'm keeping this prop for now to not break the existing code but as we implement other brands, this
   * is likely to not be used. If that's the case, we should remove this prop and the
   * corresponding styles from the codebase.
   */
  variant?: 'primary' | 'secondary';

  /**
   * Initial search suggestions to be displayed in the search popover.
   */
  initialSuggestions?: Promise<ISearchSuggestions | undefined>;
}

/**
 * Customized Search Form with Input and Svg Icon embeded.
 */
const SearchBox = observer(function SearchBox({
  className,
  initialSuggestions,
  variant = 'primary'
}: ISearchBoxProps) {
  // Reference to the form, where the <SearchPopover /> will be anchored to.
  const containerRef = useRef<HTMLDivElement | null>(null);
  // Reference to the input element, used to manage focus and blur.
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isFrameOpen, setIsFrameOpen] = useState(false);

  const [vm, { reset, navigate, search, isNavigatingToResults }] =
    useSearchBox(initialSuggestions);

  const areSearchButtonsDisabled =
    vm.term.length === 0 || isNavigatingToResults;

  const handleEnterSearch = (): void => {
    // Do not search if the input is empty
    if (vm.term.length === 0 || isNavigatingToResults) {
      return;
    }

    // Unfocus the input since the user is done typing
    inputRef?.current?.blur();
    // Set the focus state to false to close the search suggestions frame
    setIsFrameOpen(false);

    // Use helper method from `useSearchBox` to search for the term in the SearchBoxVM
    // That will navigate to the proper search page showing the results.
    navigate();
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>): void => {
    if (isNavigatingToResults) {
      // If the search navigation is already in progress, do not trigger another search
      return;
    }

    // Perform a debounced search on input change. The view model will be responsible for
    // maintain the state of the input value and perform the debounced search suggestions
    // based on the input value.
    search(e.target.value);
  };

  const handleKeyPress = (e: KeyboardEvent<HTMLInputElement>): void => {
    switch (e.key) {
      case 'Enter':
        handleEnterSearch();
        break;
      case 'Escape':
        inputRef?.current?.blur();
        break;
      default:
        break;
    }
  };

  return (
    <div
      className={classes(S.searchInputContainer, S.searchInput)}
      ref={containerRef}
    >
      <form
        role="search"
        className={classes(
          className,
          S.form,
          variant === 'primary' && S.primary,
          variant === 'secondary' && S.secondary,
          isFrameOpen && S.frameOpen
        )}
        onSubmit={(e) => e.preventDefault()}
        onFocus={() => setIsFrameOpen(true)}
        style={{ background: isFrameOpen ? '#f3f3f3' : '#fff' }}
      >
        <input
          ref={inputRef}
          title={msg(search_box_title)}
          data-testid="plpSearchInput"
          aria-label={msg(search_box_placeholder)}
          placeholder={msg(search_box_placeholder)}
          className={classes(
            S.searchInput,
            variant === 'primary' && S.primary,
            variant === 'secondary' && S.secondary,
            isFrameOpen && S.frameOpen
          )}
          value={vm.term}
          onKeyDown={handleKeyPress}
          onChange={handleInputChange}
        />
        <div className={S.buttonsRow}>
          <Button
            title={msg(search_box_clear)}
            datatestID="plpClearSearchButton"
            variant="text"
            onClick={reset}
            className={S.closeButton}
            disabled={areSearchButtonsDisabled}
            ariaLabel={msg(search_box_clear)}
          >
            <Icon
              size={IconSizeProp.Size1X}
              icon={IconTypes.CircleXMark}
              className={classes(
                S.formIcon,
                vm.term.length === 0 && S.hidden,
                variant === 'primary' && S.primary,
                variant === 'secondary' && S.secondary
              )}
            />
          </Button>
          <Button
            title={msg(search_box_search)}
            datatestID="plpSearchButton"
            variant="text"
            onClick={handleEnterSearch}
            className={S.searchButton}
            disabled={areSearchButtonsDisabled}
            ariaLabel={msg(search_box_search)}
          >
            <Icon
              size={IconSizeProp.SizeHeavy}
              icon={IconTypes.Search}
              className={classes(
                S.formIcon,
                variant === 'primary' && S.primary,
                variant === 'secondary' && S.secondary
              )}
            />
          </Button>
        </div>
      </form>
      <SearchBoxPopover
        vm={vm}
        anchorRef={containerRef}
        isOpen={isFrameOpen}
        onClose={() => {
          setIsFrameOpen(false);
        }}
      />
    </div>
  );
});

export default SearchBox;
